import React, { useMemo, useEffect, useState, useRef } from 'react';
import { Button } from 'components/ui';
import Select from 'react-select';
import { ArrowLeftIcon, DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { getCurrencyLabel, getCurrencySymbol } from 'utils/paymentFormatters.jsx';
import { getPaymentTypes } from 'services/paymentServices';

export const PaymentDetailsForm = ({
  selectedGateway,
  paymentCatalogs,
  isLoadingCatalogs,
  selectedCatalog,
  applicationForms = [],
  isLoadingApplicationForms = false,
  selectedApplicationForm = null,
  setSelectedApplicationForm,
  paymentFormData,
  isCreatingLink,
  onSelectCatalog,
  onInputChange,
  onBack,
  onCreate,
  showActions = true,
  isFeePayment = false,
  isInternational = false,
  feeBreakup = null,
  leadData = null,
}) => {
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [isLoadingPaymentTypes, setIsLoadingPaymentTypes] = useState(false);

  useEffect(() => {
    const fetchPaymentTypes = async () => {
      setIsLoadingPaymentTypes(true);
      const res = await getPaymentTypes();
      if (res?.success) {
        setPaymentTypes(res.data || []);
      }
      setIsLoadingPaymentTypes(false);
    };

    if (selectedGateway?.GatewayCode === 'MAN') {
      fetchPaymentTypes();
    }
  }, [selectedGateway]);


  const paymentMethodOptions = useMemo(() => {
    return paymentTypes.map(pt => ({
      value: pt.Code.toLowerCase(),
      label: pt.PaymentName,
      originalCode: pt.Code
    }));
  }, [paymentTypes]);

  const paymentCatalogOptions = useMemo(
    () =>
      paymentCatalogs.map((catalog) => ({
        value: catalog,
        label: catalog.AmountName,
      })),
    [paymentCatalogs]
  );

  const selectedCatalogOption = useMemo(
    () =>
      selectedCatalog
        ? {
          value: selectedCatalog,
          label: selectedCatalog.AmountName,
        }
        : null,
    [selectedCatalog]
  );

  const currencySymbol = isInternational ? '$' : '₹';
  const currencyLabel = isInternational ? 'USD' : 'INR';

  return (
    <>
      <div className="mb-4 flex items-center justify-between rounded-lg bg-primary-50 p-3 dark:bg-primary-900/20">
        <p className="text-sm font-medium text-primary-700 dark:text-primary-200">
          Gateway: {selectedGateway?.GatewayName}
        </p>
      </div>

      <div className="space-y-4">
        {isFeePayment ? (
          /* Fee Summary for Fee Payments */
          <div className="rounded-xl border border-primary-100 bg-primary-50/30 p-5 dark:border-primary-900/30 dark:bg-primary-900/10">
            <div className="mb-4 flex items-center justify-between border-b border-primary-100 pb-2 dark:border-primary-900/30">
              <span className="text-sm font-bold text-primary-800 dark:text-primary-300 uppercase tracking-tight">Fee Summary</span>
              {isInternational && (
                <span className="text-[10px] font-bold px-2 py-0.5 bg-primary-100 text-primary-700 dark:bg-primary-900/50 dark:text-primary-400 rounded-full">
                  INTERNATIONAL
                </span>
              )}
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 dark:text-dark-400 italic">Academic Fee:</span>
                <span className="font-bold text-gray-900 dark:text-dark-100">{currencySymbol} {parseFloat(feeBreakup?.academic?.amount || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500 dark:text-dark-400 italic">Hostel Fee:</span>
                <span className="font-bold text-gray-900 dark:text-dark-100">{currencySymbol} {parseFloat(feeBreakup?.hostel?.amount || 0).toLocaleString()}</span>
              </div>
              
              <div className="pt-3 mt-3 border-t border-primary-100 dark:border-primary-800 flex justify-between items-baseline">
                <span className="text-sm font-black text-primary-800 dark:text-primary-200 uppercase letter-spacing-tight">FINAL TOTAL:</span>
                <span className="text-2xl font-black text-primary-600 dark:text-primary-400">
                  {currencyLabel} {(parseFloat(feeBreakup?.academic?.amount || 0) + parseFloat(feeBreakup?.hostel?.amount || 0)).toLocaleString()}
                </span>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Payment Catalog Selection - Standard Payment */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Payment Catalog <span className="text-red-500">*</span>
              </label>
              <Select
                options={paymentCatalogOptions}
                value={selectedCatalogOption}
                onChange={onSelectCatalog}
                isLoading={isLoadingCatalogs}
                placeholder="Select a payment catalog..."
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={isCreatingLink}
              />
            </div>

            {/* Amount - Standard Payment */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Total Amount <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                  <span className="text-sm font-bold text-gray-500 dark:text-dark-400">{currencySymbol}</span>
                </div>
                <input
                  type="text"
                  inputMode="decimal"
                  pattern="[0-9]*"
                  name="amount"
                  value={paymentFormData.amount}
                  onChange={onInputChange}
                  placeholder="Enter amount"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 pl-7 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                  disabled={isCreatingLink}
                  required
                />
              </div>
            </div>

            {/* Currency - Standard Payment */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Currency <span className="text-red-500">*</span>
              </label>
              <select
                name="currency"
                value={paymentFormData.currency}
                onChange={onInputChange}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                disabled={isCreatingLink}
              >
                <option value="INR">{getCurrencyLabel('INR')}</option>
                <option value="USD">{getCurrencyLabel('USD')}</option>
              </select>
            </div>
          </>
        )}

        {/* Manual Gateway Fields */}
        {selectedGateway?.GatewayCode === 'MAN' && (
          // ... 
          <>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Payment Method <span className="text-red-500">*</span>
              </label>
              <Select
                options={paymentMethodOptions}
                value={paymentMethodOptions.find(option => option.value === paymentFormData.paymentMethod) || null}
                onChange={(option) => onInputChange({ target: { name: 'paymentMethod', value: option?.value || '' } })}
                placeholder="Select payment method..."
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={isCreatingLink}
                isLoading={isLoadingPaymentTypes}
                required
                isClearable
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Transaction ID {paymentFormData.paymentMethod !== 'cash' && <span className="text-red-500">*</span>}
              </label>
              <input
                type="text"
                name="transactionId"
                value={paymentFormData.transactionId}
                onChange={(e) => {
                  const value = e.target.value.slice(0, 20); // Limit to 20 chars
                  onInputChange({ target: { name: 'transactionId', value } });
                }}
                placeholder="Enter transaction ID"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
                disabled={isCreatingLink}
                required={paymentFormData.paymentMethod !== 'cash'}
              />
            </div>
          </>
        )}

        {/* Application Form & Description - Hidden for Fee Payment */}
        {!isFeePayment && (
          <>
            {/* Application Form Selection */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Application Form <span className="text-red-500">*</span>
              </label>
              <Select
                options={applicationForms.map(form => ({ value: form, label: form.Title || form.FormName }))}
                value={selectedApplicationForm ? { value: selectedApplicationForm, label: selectedApplicationForm.Title || selectedApplicationForm.FormName } : null}
                onChange={(option) => setSelectedApplicationForm(option?.value || null)}
                isLoading={isLoadingApplicationForms}
                placeholder="Select an application form..."
                className="react-select-container"
                classNamePrefix="react-select"
                isDisabled={isCreatingLink}
                isClearable
              />
            </div>

            {/* Description */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                name="description"
                value={paymentFormData.description}
                onChange={onInputChange}
                placeholder="Enter payment description"
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 min-h-[80px]"
                disabled={isCreatingLink}
                required
              />
            </div>
          </>
        )}
      </div>

      {/* Actions */}
      {showActions && (
        <div className="mt-6 flex gap-3">
          <Button
            variant="outlined"
            onClick={onBack}
            disabled={isCreatingLink}
            className="flex-1"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            <span className="ml-1">Back</span>
          </Button>
          <Button
            variant="solid"
            color="primary"
            onClick={onCreate}
            disabled={
              isCreatingLink || 
              !selectedCatalog || 
              !paymentFormData.amount || 
              (selectedGateway?.GatewayCode === 'MAN' && (!paymentFormData.paymentMethod || (paymentFormData.paymentMethod !== 'cash' && !paymentFormData.transactionId)))
            }
            className="flex-1"
          >
            {isCreatingLink ? (selectedGateway?.GatewayCode === 'MAN' ? 'Saving...' : 'Creating...') : (selectedGateway?.GatewayCode === 'MAN' ? 'Save Payment' : 'Create Link')}
          </Button>
        </div>
      )}
    </>
  );
};
